import type { FC } from 'react'
import { useCallback, useMemo } from 'react'
import { Button } from 'react-aria-components'
import { cx } from 'src/utils/cx'
import { getImageThumbnailUrl } from 'src/utils/media'
import { track } from 'src/utils/tracker'
import { feedToolbarButtonStyles } from '../ui/FeedToolbarButton'
import { UILink } from '../ui/Link'

export interface TagGalleryItemProps {
  name: string
  active?: boolean
  onClick?: () => any
  icon?: JSX.Element
  mediaId?: string
  thumbnailUrl?: string
  displayName?: string
  suffix?: string
  scene?: string
  to?: string
  className?: string
}

const TagGalleryItem: FC<TagGalleryItemProps> = props => {
  const {
    name,
    active,
    onClick = () => {},
    icon,
    mediaId,
    thumbnailUrl,
    displayName = name,
    suffix,
    scene,
    to,
    className,
  } = props
  const tagPreviewThumbnail = thumbnailUrl ?? getImageThumbnailUrl(mediaId)

  const handlePress = useCallback(() => {
    onClick()
    track('tag_click', {
      tag: name,
      isCancel: active,
      scene,
    })

    const mainContent = document.querySelector('#tag-gallery')
    if (mainContent) {
      mainContent.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }, [active, name, onClick, scene])

  const pillClassName = feedToolbarButtonStyles({
    active,
    class: className,
  })

  const iconElement = useMemo(() => {
    if (icon) {
      return (
        <span
          className={cx(
            'flex size-6 shrink-0 items-center justify-center',
            active ? 'text-white' : 'text-primary',
          )}
        >
          {icon}
        </span>
      )
    }

    if (!tagPreviewThumbnail) return null

    return (
      <span
        className={cx(
          'flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full border border-black/10',
          'dark:border-white/10',
          active ? 'bg-white/10' : 'bg-background-light',
        )}
      >
        <img
          src={tagPreviewThumbnail}
          alt={`${displayName} - AI art tag on PixAI`}
          loading="lazy"
          className="size-full object-cover"
        />
      </span>
    )
  }, [icon, tagPreviewThumbnail, active, displayName])

  const labelChildren = (
    <>
      {iconElement}
      <span
        className={cx(
          'min-w-0 truncate text-sm leading-tight font-normal',
          active ? 'text-white' : 'text-inherit',
        )}
      >
        {displayName}
        {suffix && <span className="ml-1 text-xs text-gray-400">{suffix}</span>}
      </span>
    </>
  )

  const labelClassName = 'flex min-w-0 items-center gap-2'

  if (to) {
    return (
      <UILink
        href={to}
        className={pillClassName}
        title={displayName}
        onPress={handlePress}
        labelClassName={labelClassName}
      >
        {labelChildren}
      </UILink>
    )
  }

  return (
    <Button className={pillClassName} onPress={handlePress} type="button">
      <span className={labelClassName} title={displayName}>
        {labelChildren}
      </span>
    </Button>
  )
}

export default TagGalleryItem
